<template>
  <div>
    <NavigationBar
      titel="Ressourcenplaner"
      isPreview
      showBackBtn
      :actions="actions"
    ></NavigationBar>
    <section>
      <v-snackbar v-model="snackbar.model" timeout="3000">{{
        snackbar.text
      }}</v-snackbar>
    </section>
    <section v-if="mobile">
      <v-container class="fill-height mt-16">
        <v-row align="top" justify="center">
          <v-col cols="12" sm="12" md="8">
            <v-card>
              <v-card-title class="d-flex justify-center"
                ><div class="text-center">
                  <v-avatar color="error" rounded size="70" class="mb-4">
                    <v-icon x-large color="white"> mdi-cellphone-off</v-icon>
                  </v-avatar>
                  <div>Bildschirm zu klein für diese Funktion</div>
                </div>
              </v-card-title>
              <v-card-subtitle class="text-center"
                >Diese Ansicht steht nur auf größeren Bildschirmen zur
                Verfügung.</v-card-subtitle
              >
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>
    <section v-else>
      <template>
        <v-navigation-drawer
          app
          bottom
          clipped
          width="350px"
          height="100vh"
          permanent
          touchless
        >
          <div class="mx-3 my-2">
            <v-text-field
              v-model="membershipsSearch"
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              label="Nach Personen suchen"
              clearable
              hide-details="auto"
            ></v-text-field>
          </div>

          <v-divider class="mt-3"></v-divider>
          <v-data-table
            :items="members"
            :headers="headers"
            :search="membershipsSearch"
            :group-by="groupBy"
            :group-desc="false"
            :sort-desc="false"
            sort-by="user.displayName"
            dense
            fixed-header
            disable-pagination
            hide-default-footer
            mobile-breakpoint="0"
          >
            <template v-slot:[`header.workload`]="{}">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small v-bind="attrs" v-on="on"
                    >mdi-briefcase-upload-outline</v-icon
                  >
                </template>
                <span>Übernommener Aufwand</span>
              </v-tooltip>
            </template>
            <template v-slot:[`header.count`]="{}">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small v-bind="attrs" v-on="on">mdi-sigma</v-icon>
                </template>
                <span>Summe an Zuweisungen</span>
              </v-tooltip>
            </template>
            <template v-slot:[`group.header`]="{ items, isOpen, toggle }">
              <th :colspan="headers.length" class="group-header-th">
                <v-icon @click="toggle" class="mr-2"
                  >{{ isOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}
                </v-icon>
                <span>
                  {{ items[0].group.title }}
                </span>
              </th>
            </template>
            <template v-slot:[`item.workload`]="{ item }">
              <assignment-status-chip
                showCount
                hideInfoText
                :count="item.workloadScore"
                :minThreshold="assignmentsConfig.thresholds.minWorkloadCount"
                :maxThreshold="assignmentsConfig.thresholds.maxWorkloadCount"
                small
              ></assignment-status-chip>
            </template>
            <template v-slot:[`item.count`]="{ item }">
              <assignment-status-chip
                showCount
                hideInfoText
                :count="item.assignmentCount"
                :minThreshold="assignmentsConfig.thresholds.minAssignmentsCount"
                :maxThreshold="assignmentsConfig.thresholds.maxAssignmentsCount"
                small
              ></assignment-status-chip>
            </template>
            <template v-slot:top>
              <v-btn
                depressed
                class="ml-3 my-2"
                small
                @click="toggleThresholdsDialog"
                ><v-icon left dense>mdi-counter</v-icon>Schwellenwerte
                anpassen</v-btn
              >
              <v-divider></v-divider>
            </template>
          </v-data-table>
        </v-navigation-drawer>
        <v-container fluid>
          <v-row>
            <v-col cols="12">
              <v-card>
                <v-card-title>
                  <!-- Search bar for assignments -->
                  <v-text-field
                    v-model="assignmentsSearch"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    label="Aufgabenbereiche durchsuchen"
                    clearable
                    hide-details="auto"
                    style="max-width: 400px"
                  ></v-text-field>
                  <v-spacer></v-spacer>
                  <v-menu
                    offset-y
                    left
                    :close-on-content-click="false"
                    max-width="400"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        small
                        depressed
                        v-bind="attrs"
                        v-on="on"
                        :color="notificationsPaused ? 'warning' : ''"
                      >
                        <v-icon left small>{{
                          notificationsPaused ? "mdi-bell-off" : "mdi-bell"
                        }}</v-icon>
                        {{
                          notificationsPaused
                            ? "Benachrichtigungen deaktiviert"
                            : "Benachrichtigungen"
                        }}
                      </v-btn>
                    </template>
                    <v-card>
                      <v-card-text v-if="!notificationsPaused">
                        <div class="text-body-2">
                          <strong>Tipp:</strong> Bei umfangreichen Änderungen
                          kannst du E-Mail-Benachrichtigungen deaktivieren, um
                          Mitglieder nicht sofort zu benachrichtigen.
                        </div>
                        <v-btn
                          small
                          color="primary"
                          depressed
                          block
                          class="mt-3"
                          @click="disableNotifications()"
                          :loading="pauseLoading"
                        >
                          <v-icon left small>mdi-bell-off</v-icon>
                          Benachrichtigungen deaktivieren
                        </v-btn>
                      </v-card-text>
                      <v-card-text v-else>
                        <div class="text-body-2">
                          <v-icon small class="mr-1">mdi-bell-off</v-icon>
                          <strong>Benachrichtigungen sind deaktiviert.</strong>
                          Mitglieder werden über Änderungen nicht
                          benachrichtigt.
                        </div>
                        <v-btn
                          small
                          text
                          block
                          class="mt-3"
                          :to="{
                            name: 'member-management-config-assignments',
                            params: {
                              organizationId: $route.params.organizationId,
                            },
                          }"
                        >
                          Einstellungen öffnen
                          <v-icon right small>mdi-open-in-new</v-icon>
                        </v-btn>
                      </v-card-text>
                    </v-card>
                  </v-menu>
                </v-card-title>
              </v-card>
            </v-col>
          </v-row>
        </v-container>

        <v-container fluid class="overflow-x-auto">
          <v-row justify="center" align="center">
            <v-col cols="12">
              <v-data-iterator
                :items="assignments"
                :search="assignmentsSearch"
                item-key="meta.id"
                sort-by="title"
                no-data-text="Keine Aufgabenbereiche vorhanden."
                no-results-text="Keine Aufgabenbereiche gefunden."
                hide-default-footer
                :items-per-page="-1"
              >
                <template v-slot:default="props">
                  <v-row class="flex-nowrap">
                    <v-col
                      v-for="assignment in props.items"
                      :key="assignment.meta.id"
                      cols="12"
                      md="5"
                      sm="8"
                    >
                      <v-card class="d-flex flex-column" height="600px">
                        <v-card-title class="py-2">
                          <data-avatar
                            :icon="assignment.icon"
                            :color="assignment.color"
                            size="34"
                            small
                          ></data-avatar>
                          <span class="ml-4 text-subtitle-1 font-weight-medium">
                            {{ assignment.title }}
                          </span>
                          <v-spacer></v-spacer>
                          <assignment-status-chip
                            :count="assignment.assignees.length"
                            :minThreshold="assignment.analysis.minAssignees"
                            :maxThreshold="assignment.analysis.maxAssignees"
                            small
                          ></assignment-status-chip>
                        </v-card-title>
                        <v-card-subtitle class="pt-3 pb-2">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-chip
                                label
                                x-small
                                outlined
                                v-bind="attrs"
                                v-on="on"
                                class="mr-1"
                              >
                                <v-icon x-small left
                                  >mdi-briefcase-upload-outline</v-icon
                                >
                                <span>{{ assignment.workloadScore }}</span>
                              </v-chip>
                            </template>
                            <span>Aufwandswert</span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-chip
                                label
                                x-small
                                outlined
                                v-bind="attrs"
                                v-on="on"
                                class="mr-1"
                              >
                                <v-icon x-small left>mdi-dumbbell</v-icon>
                                <span>{{ assignment.priorityScore }}</span>
                              </v-chip>
                            </template>
                            <span>Priorität</span>
                          </v-tooltip>
                          <span class="text-caption grey--text">
                            Min.: {{ assignment.analysis.minAssignees }}, Max.:
                            {{ assignment.analysis.maxAssignees }}
                          </span>
                        </v-card-subtitle>
                        <v-divider></v-divider>

                        <!-- Assignee list -->
                        <v-card-text
                          class="flex-grow-1 overflow-y-auto py-1 px-2"
                        >
                          <v-card
                            outlined
                            v-for="assignee in sortAssignees(
                              assignment.assignees,
                            )"
                            :key="assignee.user.uid"
                            class="my-1"
                            :ripple="false"
                          >
                            <v-list-item link class="px-2 rounded">
                              <v-list-item-content class="py-1">
                                <v-list-item-title
                                  class="text-body-2 font-weight-medium"
                                >
                                  {{ assignee.user.displayName }}
                                  <v-icon
                                    v-if="assignee.isLeader"
                                    color="amber darken-2"
                                    class="ml-1"
                                    x-small
                                    >mdi-star</v-icon
                                  >
                                </v-list-item-title>
                                <v-list-item-subtitle
                                  v-if="assignee.position"
                                  class="text-caption"
                                >
                                  {{ assignee.position }}
                                </v-list-item-subtitle>
                              </v-list-item-content>
                              <v-list-item-action-text
                                class="my-0 flex-row"
                                style="gap: 2px"
                              >
                                <v-btn
                                  icon
                                  small
                                  @click="
                                    openEditAssigneeDialog(assignee, assignment)
                                  "
                                >
                                  <v-icon small>mdi-pencil</v-icon>
                                </v-btn>
                                <v-btn
                                  icon
                                  small
                                  color="error"
                                  @click="deleteAssignee(assignee, assignment)"
                                >
                                  <v-icon small>mdi-close</v-icon>
                                </v-btn>
                              </v-list-item-action-text>
                            </v-list-item>
                          </v-card>

                          <!-- Empty state -->
                          <div
                            v-if="assignment.assignees.length === 0"
                            class="text-center py-6 grey--text"
                          >
                            <v-icon color="grey lighten-1" class="mb-2"
                              >mdi-account-group-outline</v-icon
                            >
                            <div class="text-caption">Keine Zuweisungen</div>
                          </div>
                        </v-card-text>

                        <v-divider></v-divider>

                        <!-- Add assignee -->
                        <v-card-actions class="pa-3">
                          <v-autocomplete
                            v-model="assignment.newAssignee"
                            :items="getAssigneesForSelection(assignment)"
                            outlined
                            label="Person hinzufügen"
                            dense
                            return-object
                            hide-details="auto"
                            item-text="displayName"
                            item-value="uid"
                            item-disabled="disabled"
                            prepend-inner-icon="mdi-account-search"
                            no-data-text="Keine Personen verfügbar"
                            class="flex-grow-1"
                          >
                            <template v-slot:item="{ item }">
                              <v-list-item-avatar class="my-0 py-0" tile>
                                <v-avatar
                                  color="blue-grey lighten-4"
                                  size="28"
                                  rounded
                                >
                                  <span
                                    class="blue-grey--text text--darken-2 text-caption font-weight-bold"
                                  >
                                    {{ getInitials(item.displayName) }}
                                  </span>
                                </v-avatar>
                              </v-list-item-avatar>
                              <v-list-item-content>
                                <v-list-item-title
                                  :class="{ 'grey--text': item.disabled }"
                                  >{{ item.displayName }}</v-list-item-title
                                >
                                <v-list-item-subtitle
                                  v-if="item.disabled"
                                  class="text-caption"
                                  >Bereits zugewiesen</v-list-item-subtitle
                                >
                              </v-list-item-content>
                            </template>
                          </v-autocomplete>
                          <v-btn
                            depressed
                            color="success"
                            class="ml-2"
                            @click="addAssignee(assignment)"
                            :disabled="!assignment.newAssignee"
                          >
                            <v-icon>mdi-plus</v-icon>
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-col>
                  </v-row>
                </template>
              </v-data-iterator>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </section>

    <!-- Edit Assignee Dialog -->
    <custom-dialog
      v-if="editAssigneeDialog.show"
      @close="closeEditAssigneeDialog()"
      title="Zuweisung bearbeiten"
      width="s"
      :btnAction="saveEditAssignee"
      btnText="Bestätigen"
    >
      <template v-slot:content>
        <v-container>
          <v-row>
            <v-col cols="12">
              <v-list-item class="px-0">
                <v-list-item-avatar tile>
                  <v-avatar color="blue-grey lighten-4" size="40" rounded>
                    <div
                      class="text-subtitle-2 font-weight-bold blue-grey--text text--darken-2"
                    >
                      {{
                        getInitials(
                          editAssigneeDialog.assignee
                            ? editAssigneeDialog.assignee.user.displayName
                            : "",
                        )
                      }}
                    </div>
                  </v-avatar>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">
                    {{
                      editAssigneeDialog.assignee
                        ? editAssigneeDialog.assignee.user.displayName
                        : ""
                    }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ editAssigneeDialog.assignmentTitle }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="editAssigneeDialog.position"
                outlined
                label="Position im Aufgabengebiet"
                hint="Wird zusammen mit dem Namen angezeigt."
                persistent-hint
                prepend-inner-icon="mdi-badge-account-horizontal"
                clearable
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-card outlined class="pa-3">
                <v-checkbox
                  v-model="editAssigneeDialog.isLeader"
                  hide-details="auto"
                  class="mt-0"
                >
                  <template #label>
                    <div>
                      <div class="text-body-2 font-weight-medium">
                        Leitung des Aufgabengebiets
                      </div>
                      <div class="text-caption grey--text">
                        Die Position wird als Leitung gekennzeichnet und
                        hervorgehoben dargestellt.
                      </div>
                    </div>
                  </template>
                </v-checkbox>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </custom-dialog>

    <!-- Thresholds Dialog -->
    <custom-dialog
      v-if="helpers.thresholds.dialogModel"
      @close="toggleThresholdsDialog()"
      title="Schwellenwerte anpassen"
      text="Anhand der festgelegten Schwellenwerte werden für alle Mitglieder entsprechende Statusanzeigen (Optimal = Grün, Defizit = Orange, Überhang = Rot) berechnet."
      width="s"
      :errorMessage="helpers.thresholds.errorMessage"
      :btnAction="updateThresholds"
      :btnDisabled="!helpers.thresholds.formIsValid"
      :loading="helpers.thresholds.loading"
      btnText="Speichern"
    >
      <template v-slot:content>
        <v-form
          v-model="helpers.thresholds.formIsValid"
          @submit.prevent="updateThresholds()"
        >
          <v-card outlined class="mt-2">
            <v-card-subtitle class="font-weight-medium"
              ><v-icon dense class="mr-2">mdi-briefcase-upload-outline</v-icon
              >Übernommener Aufwand</v-card-subtitle
            >
            <v-card-text>
              <v-row>
                <v-col cols="6">
                  <v-text-field
                    v-model.number="
                      assignmentsConfig.thresholds.minWorkloadCount
                    "
                    label="Minimum"
                    dense
                    hide-details="auto"
                    :rules="[rules.required]"
                    outlined
                    type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model.number="
                      assignmentsConfig.thresholds.maxWorkloadCount
                    "
                    label="Maximum"
                    dense
                    hide-details="auto"
                    :rules="[rules.required]"
                    outlined
                    type="number"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-subtitle class="font-weight-medium"
              ><v-icon dense class="mr-2">mdi-sigma</v-icon>Zugewiesene
              Aufgabenbereiche</v-card-subtitle
            ><v-card-text>
              <v-row>
                <v-col cols="6">
                  <v-text-field
                    v-model.number="
                      assignmentsConfig.thresholds.minAssignmentsCount
                    "
                    label="Minimum"
                    dense
                    hide-details="auto"
                    :rules="[rules.required]"
                    outlined
                    type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model.number="
                      assignmentsConfig.thresholds.maxAssignmentsCount
                    "
                    label="Maximum"
                    dense
                    hide-details="auto"
                    :rules="[rules.required]"
                    outlined
                    type="number"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-form>
      </template>
    </custom-dialog>
  </div>
</template>

<script>
import NavigationBar from "@/components/_systemwide/NavigationBar.vue";
import DataAvatar from "@/components/_systemwide/DataAvatar.vue";
import AssignmentStatusChip from "@/components/member-management/assignments/AssignmentStatusChip.vue";
import CustomDialog from "@/components/_systemwide/CustomDialog.vue";
import { MEMBER_MANAGEMENT, ADMIN } from "@/store/modules.js";
import {
  GET_ASSIGNMENTS,
  GET_USER_LIST,
  UPDATE_ASSIGNMENTS,
  UPDATE_MEMBER_MANAGEMENT_CONFIG,
} from "@/store/action-types.js";

export default {
  name: "member-management-assignments-planner",
  components: {
    NavigationBar,
    DataAvatar,
    AssignmentStatusChip,
    CustomDialog,
  },
  data() {
    return {
      groupBy: "group.title",
      membershipsSearch: "",
      assignmentsSearch: "",
      rules: {
        required: (value) => !!value || "Feld ist erforderlich.",
      },
      snackbar: {
        model: false,
        text: "",
      },
      helpers: {
        thresholds: {
          dialogModel: false,
          formIsValid: false,
          loading: false,
          errorMessage: "",
        },
        formIsValid: false,
      },
      editAssigneeDialog: {
        show: false,
        assignee: null,
        assignment: null,
        assignmentTitle: "",
        position: "",
        isLeader: false,
      },

      headers: [
        { text: "Name", value: "user.displayName", divider: true },
        { text: "Aufwand", value: "workload" },
        { text: "Zuweisungen", value: "count" },
      ],
      pauseLoading: false,
    };
  },
  computed: {
    mobile() {
      return this.$vuetify.breakpoint.sm || this.$vuetify.breakpoint.xs;
    },
    actions() {
      if (!this.mobile) {
        return [
          {
            title: "Speichern",
            icon: "mdi-content-save",
            actionStyle: "textBtnSubmit",
            function: this.updateAssignments,
          },
        ];
      } else {
        return [];
      }
    },
    assignmentsConfig() {
      return this.$store.state.organization.activeOrganization.config
        .memberManagement.assignments;
    },
    assignments() {
      return this.$store.state.memberManagement.assignments;
    },
    members() {
      const input = this.$store.state.admin.membershipsPublic;
      const result = input.map((content) => ({
        ...content,
        searchableDisplayName: content.user.displayName,
        workloadScore: this.getWorkloadScoreByUid(content.user.uid),
        assignmentCount: this.getAssignmentCountByUid(content.user.uid),
      }));
      return result;
    },
    membershipsForAssigneeSelection() {
      var result = this.$store.state.admin.membershipsPublic.map((item) => {
        const container = {
          displayName: item.user.displayName,
          uid: item.user.uid,
        };
        return container;
      });
      return result;
    },
    noDuplicateAssignees() {
      return this.assignments.some((assignment) => {
        return this.checkForDuplicates(assignment.assignees);
      });
    },
    notificationsPaused() {
      return this.$store.state.organization.activeOrganization.config
        .memberManagement.assignments.notificationsPaused;
    },
  },
  created() {
    this.initialize();
  },
  methods: {
    getInitials(name) {
      if (!name) return "?";
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return parts[0].substring(0, 2).toUpperCase();
    },
    sortAssignees(assignees) {
      return [...assignees].sort((a, b) => b.isLeader - a.isLeader);
    },
    openEditAssigneeDialog(assignee, assignment) {
      this.editAssigneeDialog = {
        show: true,
        assignee: assignee,
        assignment: assignment,
        assignmentTitle: assignment.title,
        position: assignee.position || "",
        isLeader: assignee.isLeader || false,
      };
    },
    closeEditAssigneeDialog() {
      this.editAssigneeDialog = {
        show: false,
        assignee: null,
        assignment: null,
        assignmentTitle: "",
        position: "",
        isLeader: false,
      };
    },
    saveEditAssignee() {
      const assignee = this.editAssigneeDialog.assignee;
      if (assignee) {
        assignee.position = this.editAssigneeDialog.position || "";
        assignee.isLeader = this.editAssigneeDialog.isLeader;
        this.snackbar.text = `${assignee.user.displayName} aktualisiert`;
        this.snackbar.model = true;
      }
      this.closeEditAssigneeDialog();
    },
    async disableNotifications() {
      this.pauseLoading = true;
      try {
        await this.$store.dispatch(
          `${MEMBER_MANAGEMENT}${UPDATE_MEMBER_MANAGEMENT_CONFIG}`,
          {
            organizationId: this.$route.params.organizationId,
            type: "onlyAssignmentsConfig",
            notificationsPaused: true,
          },
        );

        this.snackbar.model = true;
        this.snackbar.text = "Benachrichtigungen deaktiviert";
      } catch (error) {
        console.error("Error disabling notifications:", error);
        this.snackbar.model = true;
        this.snackbar.text = "Fehler beim Deaktivieren der Benachrichtigungen";
      } finally {
        this.pauseLoading = false;
      }
    },
    checkForDuplicates(assignees) {
      const assigneesUids = assignees.map((item) => item.user.uid);
      const hasDuplicates = (array) => new Set(array).size !== array.length;
      return hasDuplicates(assigneesUids);
    },
    initialize() {
      this.$store.dispatch(`${MEMBER_MANAGEMENT}${GET_ASSIGNMENTS}`, {
        organizationId: this.$route.params.organizationId,
      });
      this.$store.dispatch(`${ADMIN}${GET_USER_LIST}`, {
        organizationId: this.$route.params.organizationId,
        queryIsFiltered: true,
        query: {
          type: "active",
        },
      });
    },
    toggleThresholdsDialog() {
      this.helpers.thresholds.dialogModel =
        !this.helpers.thresholds.dialogModel;
    },
    updateThresholds() {
      this.helpers.thresholds.loading = true;
      if (this.helpers.thresholds.formIsValid) {
        this.$store
          .dispatch(`${MEMBER_MANAGEMENT}${UPDATE_MEMBER_MANAGEMENT_CONFIG}`, {
            organizationId: this.$route.params.organizationId,
            type: "onlyAssignmentsThresholdsConfig",

            thresholds: {
              minWorkloadCount:
                this.assignmentsConfig.thresholds.minWorkloadCount,
              maxWorkloadCount:
                this.assignmentsConfig.thresholds.maxWorkloadCount,
              minAssignmentsCount:
                this.assignmentsConfig.thresholds.minAssignmentsCount,
              maxAssignmentsCount:
                this.assignmentsConfig.thresholds.maxAssignmentsCount,
            },
          })
          .then(() => {
            this.helpers.thresholds.loading = false;
            this.snackbar.model = true;
            this.snackbar.text = "Schwellenwerte erfolgreich aktualisiert.";
            this.toggleThresholdsDialog();
          })
          .catch((error) => {
            console.log(error);
            this.helpers.thresholds.errorMessage = error.message;
            this.helpers.thresholds.loading = false;
          });
      } else {
        alert("Prüfe Deine Eingabe und versuche es erneut.");
      }
    },
    getWorkloadScoreByUid(uid) {
      const assignmentsWithWorkload = this.assignments.map((assignment) => {
        return {
          assignees: assignment.assignees.map((assignee) => {
            return {
              ...assignee,
              workloadScore: assignment.workloadScore,
            };
          }),
        };
      });
      const allAssignments = assignmentsWithWorkload
        .map((assignment) => assignment.assignees)
        .flat();

      const assignmentsOfUser = allAssignments.filter(
        (item) => item.user.uid === uid,
      );
      // Sum up workloadScore of all assignments of user
      const workloadScore = assignmentsOfUser.reduce(
        (a, b) => a + b.workloadScore,
        0,
      );
      return workloadScore;
    },
    getAssignmentCountByUid(uid) {
      const allAssignments = this.assignments
        .map((assignment) => assignment.assignees)
        .flat();
      return allAssignments.filter((item) => item.user.uid === uid).length;
    },
    getAssigneesForSelection(assignment) {
      const assignedUids = new Set(assignment.assignees.map((a) => a.user.uid));
      return this.membershipsForAssigneeSelection.map((item) => ({
        ...item,
        disabled: assignedUids.has(item.uid),
      }));
    },
    addAssignee(assignment) {
      if (assignment.newAssignee) {
        assignment.assignees.push({
          user: {
            displayName: assignment.newAssignee.displayName,
            uid: assignment.newAssignee.uid,
          },
          dutyPosition: "",
          position: "",
          isLeader: false,
        });
        assignment.newAssignee = null;
      }
    },
    deleteAssignee(assignee, assignment) {
      assignment.assignees.splice(assignment.assignees.indexOf(assignee), 1);
    },
    updateAssignments() {
      if (this.noDuplicateAssignees) {
        alert(
          "Fehler: Mitglieder dürfen einem Aufgabengebiet nicht mehrfach zugeordnet werden.",
        );
      } else {
        this.$store
          .dispatch(`${MEMBER_MANAGEMENT}${UPDATE_ASSIGNMENTS}`, {
            organizationId: this.$route.params.organizationId,

            assignments: this.assignments,
          })
          .catch((error) => {
            alert(
              "Es ist ein Fehler aufgetreten. \n\nKontaktiere den Support mit folgender Meldung\n" +
                error,
            );
            console.error(error);
          });
      }
    },
  },
};
</script>
